import React, { useCallback, useMemo } from "react";
import { APIError } from "../../components/APIError";
import { useFallbackUrl, useNavigator } from "../../components/AppHistory";
import { CustomDialog, Field } from "../../components/CustomDialog";
import { useParams } from "react-router-dom";
import {
  Box,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@material-ui/core";
import { BillIcon, CloseIcon } from "../../icons";
import { ModalComponent } from "../../pages/routes";
import { useAccountBill } from "./hooks/useAccountBill";
import { formatBillPeriod } from "./utils/formatBillPeriod";
import { formatDate } from "../../utils/formatDate";
import { formatBillType } from "./utils/formatBillType";
import { BillRowChips } from "./components/BillRowChips";
import { BreakdownTable } from "./components/BillBreakdown";
import { formatMoneyWithCR } from "../../utils/formatMoney";
import { InlineType } from "../../components/InlineType";
import { getChipCount } from "../../api/services";
import { DialogLoader } from "../../components/Loader";

export const BillDetails: ModalComponent = ({ fallbackUrl }) => {
  useFallbackUrl(fallbackUrl);
  const theme = useTheme();
  const { closeOverlay } = useNavigator();
  const { accountNumber, billNumber } = useParams();

  const { data: bill, error, isLoading } = useAccountBill(
    accountNumber,
    billNumber,
  );

  const onCancel = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  const { currency, grossAmount } = bill || {};
  const totalAmount = useMemo(
    () =>
      grossAmount === undefined
        ? undefined
        : formatMoneyWithCR(currency, grossAmount),
    [currency, grossAmount],
  );

  return (
    <CustomDialog onClose={onCancel}>
      <DialogTitle data-testid="BillDetails__Title">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="no-wrap"
        >
          <Box>
            <Box display="flex" alignItems="center">
              <Box mr={0.5} lineHeight={0}>
                <BillIcon height={24} width={24} />
              </Box>
              <Typography variant="h6">Bill</Typography>
            </Box>
            <Typography variant="body1">{billNumber}</Typography>
          </Box>
          <Box mr={-1.5}>
            <IconButton
              onClick={onCancel}
              data-testid="BillDetails__Button--close"
            >
              <CloseIcon fill={theme.palette.text.primary} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers data-testid="BillDetails__Content">
        {error ? (
          <APIError
            title="We can't load details for this bill at the moment."
            mb={45.5}
          />
        ) : isLoading ? (
          <DialogLoader mb={37} />
        ) : (
          bill && (
            <>
              {bill.billProps && getChipCount(bill.billProps) > 0 && (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  alignItems="center"
                  mb={3}
                  flexGrow={1}
                >
                  <BillRowChips {...bill.billProps} />
                </Box>
              )}
              {bill.billProps?.isDunningSuppressed && (
                <Box mb={3}>
                  <Typography variant="body2" component="span">
                    <InlineType fontWeight="bold">
                      Dunning suppressed
                    </InlineType>
                  </Typography>
                </Box>
              )}
              <Grid container justify="space-between">
                <Field
                  label="Issued"
                  value={formatDate(bill.issueDt)}
                  data-testid="Detail__Issued--value"
                />
                <Field
                  label={
                    bill.transactionStatus === "Rejected"
                      ? "Rejected"
                      : "Accepted"
                  }
                  value={formatDate(bill.resolvedDttm)}
                  data-testid="Detail__Resolved--value"
                />
                <Field
                  label="Type"
                  value={formatBillType(bill.type)}
                  data-testid="Detail__Type--value"
                />
                <Field
                  label="Period"
                  value={formatBillPeriod(bill.fromDttm, bill.toDttm)}
                  data-testid="Detail__Period--value"
                />
                <Field
                  label="Status"
                  value={bill.status}
                  data-testid="Detail__Status--value"
                />
                <Field
                  label="Amount (incl. sales tax)"
                  value={totalAmount}
                  data-testid="Detail__Amount--value"
                />
                <Field
                  label="Due"
                  value={formatDate(bill?.dueDt)}
                  data-testid="Detail__Due--value"
                />
                <Field
                  label="Bill Balance"
                  value={formatMoneyWithCR(
                    bill.currency,
                    bill.balance || "0.00",
                  )}
                  data-testid="Detail__Balance--value"
                />
                {/* If either collection field is populated show both fields */}
                {(bill.collectionDt || bill.collectionRequestDt) && (
                  <>
                    <Field
                      label="Collection request date"
                      value={
                        bill.collectionRequestDt
                          ? formatDate(bill.collectionRequestDt)
                          : "-"
                      }
                      data-testid="Detail__ColReqDate--value"
                    />
                    <Field
                      label="Collection date"
                      value={
                        bill.collectionDt ? formatDate(bill.collectionDt) : "-"
                      }
                      data-testid="Detail__ColDate--value"
                    />
                  </>
                )}
                <Field
                  label="Draft reason"
                  value={bill.draftReason}
                  data-testid="Detail__DraftReason--value"
                  fullWidth
                  hideEmpty
                />
              </Grid>
              {bill.breakdownGroups.length > 0 && (
                <Box my={3}>
                  <BreakdownTable
                    caption="BILL BREAKDOWN"
                    totalAmount={totalAmount!}
                    breakdownGroups={bill.breakdownGroups}
                  />
                </Box>
              )}
            </>
          )
        )}
      </DialogContent>
    </CustomDialog>
  );
};
